import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import reportsPlaceholderImg from "assets/img/reports-new/reports-placeholder.svg";
import { BackButton } from "components/grid";
import { ReportType } from "constants/reports/report-type";
import { getCurrentReportTrackingDetails, trackReportAccessed } from "helpers/analytics-reports";
import { getRouteBackButtonUrl, getRouteByPathname, getRouteTitleByPathname } from "helpers/reports";
import { IKeyValue } from "helpers/url";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { ISideNavigationItem } from "interfaces/sideNavigationItem";
import { selectIsAdmin } from "store/entities/agents";
import { selectFeatureTogglePerformanceReport, selectHasAuditLog, selectHasReportExportOption } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { getTagsUsageAllowed } from "store/features/session";
import { UrlQueryParamsFeatureActions } from "store/features/url-query-params/actions";
import { UrlQueryParamsView } from "store/features/url-query-params/interfaces";
import { ReportsViewActions, ReportsViewActionsNames, isInited } from "store/views/reports";
import { buildReportsViewQueryString, parseReportsViewURLParams } from "store/views/reports/url-query-params";

import ReportsGrid from "./ReportsGrid";
import Router from "./Router";
import { EmptyState } from "./components";
import { getNavigationItems } from "./helpers";

const ReportsComponent: FC<IWithBreakpointsProps> = ({ isMobile }) => {
  const dispatch = useDispatch();

  const history = useHistory();
  const isAdmin = useSelector(selectIsAdmin);
  const tagsUsageAllowed = useSelector(getTagsUsageAllowed);
  const hasReportExportOption = useSelector(selectHasReportExportOption);
  const hasAuditLog = useSelector(selectHasAuditLog);
  const isInitialized = useSelector(isInited);
  const hasPerformanceReport = useSelector(selectFeatureTogglePerformanceReport);
  const currentPlan = useSelector(selectCurrentSubscriptionPlan);

  useEffect(() => {
    dispatch(
      UrlQueryParamsFeatureActions.registerView({
        view: UrlQueryParamsView.Reports,
        replaceHistoryActions: [
          ReportsViewActionsNames.SET_CURRENT_VIEW,
          ReportsViewActionsNames.SET_DISTRIBUTION,
          ReportsViewActionsNames.UPDATE_FILTER,
          ReportsViewActionsNames.SET_QUERY_PARAMS_FROM_URL,
        ],
        updateQueryActions: [
          ReportsViewActionsNames.SET_CURRENT_VIEW,
          ReportsViewActionsNames.SET_DISTRIBUTION,
          ReportsViewActionsNames.UPDATE_FILTER,
          ReportsViewActionsNames.SET_QUERY_PARAMS_FROM_URL,
        ],
        isPathMatchingPattern: pathname => /reports/.test(pathname),
        setQueryParamsAction: ReportsViewActions.setQueryParamsFromURL,
        queryStringSerializer: buildReportsViewQueryString,
        queryStringParser: (queryParams: IKeyValue<string>, pathname: string) =>
          parseReportsViewURLParams(queryParams, pathname) as IKeyValue<string>,
      })
    );
    if (tagsUsageAllowed) {
      dispatch(ReportsViewActions.fetchTags());
    }
    return (): void => {
      dispatch(UrlQueryParamsFeatureActions.unregisterView({ view: UrlQueryParamsView.Reports }));
    };
  }, []);

  if (!isInitialized) {
    return null;
  }

  if (isMobile) {
    return (
      <EmptyState
        icon={reportsPlaceholderImg}
        description={
          <>
            <strong>Reports</strong> are not available on mobile devices. Please switch to a desktop browser for a
            better experience.
          </>
        }
      />
    );
  }

  const handleCurrentViewChange = (view: ReportType): void => {
    dispatch(ReportsViewActions.setCurrentView(view));
  };
  const pathname = history.location.pathname;

  const route = getRouteByPathname(pathname);
  const title = getRouteTitleByPathname(pathname);
  const backButtonUrl = getRouteBackButtonUrl(pathname);
  const backButton = backButtonUrl ? <BackButton onClick={() => handleBackToList(backButtonUrl)} /> : undefined;
  const items = getNavigationItems(hasReportExportOption, hasAuditLog, hasPerformanceReport, isAdmin);

  const handleActiveItemUpdated = (item: ISideNavigationItem): void => {
    const reportDetails = getCurrentReportTrackingDetails(item.id);
    if (reportDetails) {
      trackReportAccessed(reportDetails.category, reportDetails.name);
    }
    handleCurrentViewChange(item.id as ReportType);
  };

  const handleBackToList = (url: string): void => {
    history.push(url);
  };

  const auditLogSubtitle = (): string => {
    const retentionPeriod = currentPlan === Plan.Enterprise ? "6 months" : "30 days";
    return `Data from last ${retentionPeriod} available on your plan`;
  };

  return (
    <ReportsGrid
      title={title}
      subtitle={hasAuditLog && route === ReportType.AuditLog ? auditLogSubtitle() : undefined}
      items={items}
      route={route}
      onActiveItemUpdated={handleActiveItemUpdated}
      showBackButton={!!backButton}
      backButton={backButton}
      hasAccess={true}
    >
      <Router />
    </ReportsGrid>
  );
};

export const Reports = withBreakpoints(ReportsComponent);
